import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Info, TriangleAlert } from "lucide-react";

import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { extractTharsternReasons } from "@shared-core/data/validation";
import {
  outcomeAllowsJobCreation,
  outcomeNeedsReplacementEstimate,
  recordOutcomeIntent,
  recordOutcomeStatusBody,
  type EstimateOutcomeOption,
  type EstimateWriteIntent,
} from "@shared-core/domain/estimate-controls";

import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";

/**
 * The confirm step shared by RecordOutcomeDialog and ParkChaseDialog.
 *
 * One component because it is one write: both controls end in the same
 * setstatus call, and the consequences worth stating — what the estimate
 * reports as, whether a job can still be raised — are the same questions
 * either way. Two hand-maintained copies would drift on precisely the
 * sentences this journey exists to agree.
 *
 * Owns the write itself: calls `setEstimateStatus`, shows the failure inline
 * and keeps the dialog open if Tharstern rejects it, and only calls
 * `onConfirm` once the write has actually landed — a confirm button must
 * never look successful when nothing happened.
 *
 * There is no internal-note field here. The real `SetEstimateStatusBody`
 * carries only `status`, `custom_status_description`, `revision_estimate_id`
 * and `expired` — see `recordOutcomeStatusBody`'s own docstring. Collecting
 * text with nowhere real to put it would silently discard whatever a CSR
 * typed.
 *
 * `revision_estimate_id` is the one field this step does collect, because a
 * supersession cannot be written without it and only the person doing it knows
 * which quote replaced this one.
 */

type Props = {
  estimate: EstimateDetailViewItem;
  /** Tharstern's own integer id — never null by the time this step is
   *  reachable; see `estimateControlsPresentation`'s id gate. */
  sourceEstimateId: number;
  option: EstimateOutcomeOption;
  /** Back to the pick step, discarding the pick. */
  onBack: () => void;
  /** Called once the write has actually landed in Tharstern. */
  onConfirm: (intent: EstimateWriteIntent) => void;
};

export function OutcomeConfirmStep({
  estimate,
  sourceEstimateId,
  option,
  onBack,
  onConfirm,
}: Props) {
  const adapter = useDataAdapter();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Superseding needs the quote the work moved to. Tharstern links the two
  // itself only when the replacement was raised as a revision; when it was
  // raised standalone — which reps say is common — the link is theirs to make,
  // and until this field existed the whole group was offered as unavailable.
  const needsReplacement = outcomeNeedsReplacementEstimate(option);
  const [replacement, setReplacement] = useState("");
  const replacementId = /^\d+$/.test(replacement.trim())
    ? Number(replacement.trim())
    : null;
  const replacementIsSelf = replacementId === sourceEstimateId;
  const canConfirm =
    !needsReplacement || (replacementId !== null && !replacementIsSelf);
  const intent = recordOutcomeIntent(
    estimate,
    sourceEstimateId,
    option,
    replacementId ?? undefined,
  );

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await adapter.setEstimateStatus(
        sourceEstimateId,
        recordOutcomeStatusBody(option, replacementId ?? undefined),
      );
      toast.success(intent.effect);
      onConfirm(intent);
    } catch (err) {
      console.error("estimates.controls.record_outcome.failed", err);
      const reasons = extractTharsternReasons(err);
      setError(
        reasons.length > 0
          ? reasons.join("; ")
          : err instanceof Error
            ? err.message
            : "Tharstern did not accept this. Nothing was changed.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{option.statusRef}</DialogTitle>
        <DialogDescription>{intent.effect}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        {/* The consequence that is least guessable from the wording. */}
        <div className="rounded-md border border-border px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Reports as</span>
            <Badge
              variant={
                option.classifiesAs === "won"
                  ? "success"
                  : option.classifiesAs === "lost"
                    ? "error"
                    : "neutral"
              }
            >
              {option.classifiesAs === "still_open"
                ? "Still open"
                : option.classifiesAs}
            </Badge>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">
              Can a job still be raised in Tharstern?
            </span>
            <Badge
              variant={outcomeAllowsJobCreation(option) ? "success" : "warning"}
            >
              {outcomeAllowsJobCreation(option) ? "Yes" : "No"}
            </Badge>
          </div>
        </div>

        {needsReplacement && (
          <div className="space-y-1.5">
            <Label htmlFor="replacement-estimate">
              Replaced by estimate number
            </Label>
            <Input
              id="replacement-estimate"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 655592"
              value={replacement}
              onChange={(e) => setReplacement(e.target.value)}
              aria-invalid={replacement.trim() !== "" && !canConfirm}
              aria-describedby="replacement-estimate-help"
            />
            <p
              id="replacement-estimate-help"
              className="text-xs text-muted-foreground"
            >
              {replacementIsSelf
                ? "That is this estimate. Name the quote the work moved to."
                : replacement.trim() !== "" && replacementId === null
                  ? "Tharstern's estimate number — digits only."
                  : "Tharstern's own estimate number for the quote that replaced " +
                    "this one. It will not accept a supersession without it."}
            </p>
          </div>
        )}

        {option.classifiesAs === "still_open" && (
          // The honest sentence both dialogs are built around: a still-live
          // reason records a state and closes nothing. Said plainly, at the
          // moment of confirming.
          <p className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-xs">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />
            <span>
              This does not close the estimate. It stays open and will still age
              into &ldquo;stale&rdquo; after 90 days, exactly as if nothing had
              been recorded.
            </span>
          </p>
        )}

        {error && (
          <p className="flex items-start gap-2 rounded-md border border-[color:var(--color-error)] px-3 py-2 text-xs text-[color:var(--color-error)]">
            <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </p>
        )}
      </div>
      <DialogFooter className="sm:justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={saving}
          className="gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Pick a different reason
        </Button>
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={saving || !canConfirm}
        >
          {saving ? "Recording…" : `Record "${option.statusRef}"`}
        </Button>
      </DialogFooter>
    </>
  );
}
